import { LinkButton } from '@euler/app/components/LinkButton';
import { StackLayout } from '@euler/app/components/StackLayout';
import { OrderNavParams } from '@euler/app/flows/order/OrderFlow';
import { Icon } from '@euler/components/Icomoon';
import { Input } from '@euler/components/form/Input';
import { useShowFloatingTextInput } from '@euler/components/input/FloatingTextInput';
import { HStack } from '@euler/components/layout/Stack';
import { config } from '@euler/config';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { SafeHaptics } from '@euler/utils';
import { usePersistFn } from '@euler/utils/hooks';
import { isValidLicensePlateNo } from '@euler/utils/validators';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { memo, useCallback, useEffect } from 'react';
import { TouchableOpacity } from 'react-native';
import { useOrderFlowContext } from '../../../../OrderFlowContext';
import { ReservationAssociationSheet } from '../../components/ReservationAssociationSheet';
import { useAssociateReservationByPlate } from '../../functions/useAssociateReservationByPlate';
import { useCheckExistingOrder } from '../../functions/useCheckExistingOrder';
import { useOrderOcrScan } from '../../functions/useOrderOcrScan';
import { Cell } from '../components/Cell';
import { IconWrapper } from '../components/IconWrapper';
import { Row } from '../components/Row';
import { ScanButton } from '../components/ScanButton';
import { TextIcon } from '../components/TextIcon';
import { useOrderFormStyles } from '../components/styles';

export const LicensePlateNoRow = memo(
  ({
    required = true,
    ...props
  }: {
    required?: boolean;
    disabled?: boolean;
  }) => {
    const {
      theme,
      mode,
      formController: { form },
    } = useOrderFlowContext();

    const navigation = useNavigation<StackNavigationProp<OrderNavParams>>();

    const { iconColor, labelColor, linkColor, placeholderColor } = theme;

    const checkExistingOrder = useCheckExistingOrder();
    const {
      search: searchReservationsByPlate,
      candidates: reservationCandidates,
      select: selectReservation,
      dismiss: dismissReservations,
    } = useAssociateReservationByPlate();
    const showFloatingTextInput = useShowFloatingTextInput();

    // Search the plate's open reservations off the plate VALUE, so it also runs
    // for an initially-set plate (resumed draft / seeded) — not just manual
    // entry. Only while creating an order; associating is meaningless otherwise.
    const plateValue = form.licensePlateNo.value;
    useEffect(() => {
      if (mode === 'new') {
        void searchReservationsByPlate(plateValue);
      }
    }, [mode, plateValue, searchReservationsByPlate]);
    const styles = useOrderFormStyles();
    const { rpx } = useResponsiveContext();

    const { onScan } = useOrderOcrScan({ type: 'plate' });

    const onCellPress = usePersistFn(() => {
      SafeHaptics.selection();
      navigation.navigate('_plate', {
        licensePlateNo: form.isSpecialLicensePlateNo.value
          ? ''
          : form.licensePlateNo.value,
        onDone: value => {
          form.isSpecialLicensePlateNo.update(false);
          form.licensePlateNo.update(value);
          void checkExistingOrder('plate', value);
        },
      });
    });

    const onSpecialLicensePlateNoPress = usePersistFn(() => {
      showFloatingTextInput({
        value: form.isSpecialLicensePlateNo.value
          ? form.licensePlateNo.value
          : '',
        maxNumberOfLines: 1,
        noToolbarPlaceholder: true,
        returnKeyType: 'done',
        returnKeyLabel: '完成',
        voiceInput: {
          enablePunctuationPrediction: false,
        },
        onDone: value => {
          value = value.trim().toUpperCase();
          form.isSpecialLicensePlateNo.update(!isValidLicensePlateNo(value));
          form.licensePlateNo.update(value);
          if (value) {
            void checkExistingOrder('plate', value);
          }
        },
        placeholder: '输入任意车牌，如“临1234”，“3245警”等',
      });
    });

    const onQrcodePress = useCallback(() => {
      SafeHaptics.selection();
      navigation.navigate('_qrcodeScan', {});
    }, [navigation]);

    const onGetRandomLicensePlate = useCallback(() => {
      const value = generateNewEnergyLicensePlate();
      form.isSpecialLicensePlateNo.update(false);
      form.licensePlateNo.update(value);
      void checkExistingOrder('plate', value);
    }, [checkExistingOrder, form.isSpecialLicensePlateNo, form.licensePlateNo]);

    return (
      <>
        <Row
          errorProp="licensePlateNo"
          icon={
            <IconWrapper>
              <TextIcon text="车牌" color={iconColor} />
            </IconWrapper>
          }
          detail={
            <HStack justifyContent="flex-end">
              {mode === 'new' &&
              !form.licensePlateNo.loading &&
              form.licensePlateNo.value ? (
                <TouchableOpacity
                  onPress={onQrcodePress}
                  css={`
                    margin-right: ${rpx(12)}px;
                    top: ${rpx(1)}px;
                  `}
                >
                  <Icon name="qrcode" size={rpx(28)} color="#555" />
                </TouchableOpacity>
              ) : null}
              <ScanButton
                color={labelColor}
                onPress={onScan}
                disabled={form.licensePlateNo.loading || props.disabled}
              />
            </HStack>
          }
          onPress={onCellPress}
          {...props}
        >
          <Cell
            required={required}
            label="车牌号"
            helpTextIconHidden={true}
            helpText={
              <StackLayout direction="row" gap={rpx(10)} alignItems="center">
                {config.environment !== 'production' ? (
                  <LinkButton
                    text="生成随机车牌"
                    icon={<Icon name="arrow-right-circle" color={linkColor} />}
                    textStyle={{ fontSize: rpx(12) }}
                    onPress={onGetRandomLicensePlate}
                    disabled={props.disabled}
                  />
                ) : undefined}
                <LinkButton
                  text="使用特殊车牌"
                  textStyle={{ fontSize: rpx(12) }}
                  icon={<Icon name="plus" color={linkColor} />}
                  onPress={onSpecialLicensePlateNoPress}
                  disabled={props.disabled}
                />
              </StackLayout>
            }
          >
            <Input
              style={styles.formInput}
              placeholderTextColor={placeholderColor}
              value={form.licensePlateNo.value}
              placeholder="请输入或扫描识别车牌"
              errorMsg={form.licensePlateNo.error}
              loading={form.licensePlateNo.loading}
              editable={false}
              pointerEvents="none"
              tag="plate"
            />
          </Cell>
        </Row>
        <ReservationAssociationSheet
          reservations={reservationCandidates}
          onSelect={selectReservation}
          onClose={dismissReservations}
        />
      </>
    );
  },
);

// generated by chatgpt
function generateNewEnergyLicensePlate(): string {
  // 省份简称，可以根据需要扩展或修改
  const provinces = [
    '京',
    '沪',
    '粤',
    '津',
    '冀',
    '豫',
    '云',
    '辽',
    '黑',
    '湘',
    '皖',
    '鲁',
    '新',
    '苏',
    '浙',
    '赣',
    '鄂',
    '桂',
    '甘',
    '晋',
    '蒙',
    '陕',
    '吉',
    '闽',
    '贵',
    '川',
    '青',
    '藏',
    '琼',
  ];
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const digits = '0123456789';

  // 生成省份简称
  const province = provinces[Math.floor(Math.random() * provinces.length)];

  // 生成发牌机关代号 (1个字母)
  const issuingOffice = letters.charAt(
    Math.floor(Math.random() * letters.length),
  );

  const newEnergy = Math.random() > 0.5;

  // 随机生成小型新能源汽车或者大型新能源汽车
  const isSmallNewEnergy = Math.random() > 0.5;

  let plateNumber = '';

  if (newEnergy) {
    if (isSmallNewEnergy) {
      // 小型新能源汽车 (1个字母 + 5个数字 + 1个字母)
      const randomLetter1 = letters.charAt(
        Math.floor(Math.random() * letters.length),
      );
      const randomLetter2 = letters.charAt(
        Math.floor(Math.random() * letters.length),
      );
      plateNumber += randomLetter1; // 前面的字母

      for (let i = 0; i < 4; i++) {
        const randomDigit = digits.charAt(
          Math.floor(Math.random() * digits.length),
        );
        plateNumber += randomDigit;
      }

      plateNumber += randomLetter2; // 后面的字母
    } else {
      // 大型新能源汽车 (2个字母 + 5个数字)
      const randomLetter1 = letters.charAt(
        Math.floor(Math.random() * letters.length),
      );
      const randomLetter2 = letters.charAt(
        Math.floor(Math.random() * letters.length),
      );
      plateNumber += randomLetter1 + randomLetter2; // 前面的2个字母

      for (let i = 0; i < 4; i++) {
        const randomDigit = digits.charAt(
          Math.floor(Math.random() * digits.length),
        );
        plateNumber += randomDigit;
      }
    }
  } else {
    for (let i = 0; i < 5; i++) {
      const randomDigit = digits.charAt(
        Math.floor(Math.random() * digits.length),
      );
      plateNumber += randomDigit;
    }
  }

  // 拼接完整的车牌号
  return `${province}${issuingOffice}${plateNumber}`;
}
